import {
  createBrowserRouter,
  createRoutesFromElements,
  Route
} from "react-router-dom";
import { lazy, Suspense, type ReactElement } from "react";
import { RouteError } from "@/components/common/RouteError";
import { ProtectedRoute, AdminGate } from "@/routes/guards";
import { RootLayout } from "@/components/layout/RootLayout";

const LoadingFallback = (
  <div className="flex min-h-[40vh] mt-4 items-center justify-center text-sm text-muted-foreground">
    Chargement en cours…
  </div>
);

const withSuspense = (element: ReactElement) => (
  <Suspense fallback={LoadingFallback}>{element}</Suspense>
);

const Index = lazy(() => import("@/pages/Index"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const Blog = lazy(() => import("@/pages/Blog"));
const BlogPost = lazy(() => import("@/pages/BlogPost"));
const BlogAdmin = lazy(() => import("@/pages/BlogAdmin"));
const Games = lazy(() => import("@/pages/Games"));
const AIProvidersAdmin = lazy(() => import("@/pages/AIProvidersAdmin"));
const ImageManager = lazy(() => import("@/pages/ImageManager"));
const GamesAdmin = lazy(() => import("@/pages/admin/GamesAdmin"));
const AdminLayout = lazy(() => import("@/components/features/admin/AdminLayout"));
const Dashboard = lazy(() => import("@/components/features/admin/Dashboard"));
const ContentManager = lazy(() => import("@/components/features/admin/ContentManager"));
const SEOManager = lazy(() => import("@/components/features/admin/SEOManager"));
const SEOManagement = lazy(() => import("@/pages/SEOManagement"));
const SMTPManager = lazy(() => import("@/components/features/admin/SMTPManager"));
const Logs = lazy(() => import("@/components/features/admin/Logs"));
const NewsletterManager = lazy(() => import("@/components/features/admin/NewsletterManager"));
const Analytics = lazy(() => import("@/pages/Analytics"));
const TestHybridReader = lazy(() => import("@/pages/TestHybridReader"));

export const router = createBrowserRouter(
  createRoutesFromElements(
    <Route element={<RootLayout />}>
      <Route path="/" element={withSuspense(<Index />)} errorElement={<RouteError />} />
      <Route path="/blog" element={withSuspense(<Blog />)} errorElement={<RouteError />} />
      <Route path="/blog/:slug" element={withSuspense(<BlogPost />)} errorElement={<RouteError />} />
      <Route path="/test-hybrid-reader" element={withSuspense(<TestHybridReader />)} errorElement={<RouteError />} />
      <Route path="/games" element={withSuspense(<Games />)} errorElement={<RouteError />} />
      <Route path="/admin" element={withSuspense(<AdminGate />)} errorElement={<RouteError />} />
      <Route element={withSuspense(<ProtectedRoute><AdminLayout /></ProtectedRoute>)} errorElement={<RouteError />}>
        <Route path="/admin/dashboard" element={withSuspense(<Dashboard />)} errorElement={<RouteError />} />
        <Route path="/admin/analytics" element={withSuspense(<Analytics />)} errorElement={<RouteError />} />
        <Route path="/admin/content" element={withSuspense(<ContentManager />)} errorElement={<RouteError />} />
        <Route path="/admin/seo" element={withSuspense(<SEOManager />)} errorElement={<RouteError />} />
        <Route path="/admin/seo-management" element={withSuspense(<SEOManagement />)} errorElement={<RouteError />} />
        <Route path="/admin/newsletter" element={withSuspense(<NewsletterManager />)} errorElement={<RouteError />} />
        <Route path="/admin/smtp" element={withSuspense(<SMTPManager />)} errorElement={<RouteError />} />
        <Route path="/admin/logs" element={withSuspense(<Logs />)} errorElement={<RouteError />} />
        <Route path="/admin/blog" element={withSuspense(<BlogAdmin />)} errorElement={<RouteError />} />
        <Route path="/admin/blog/edit/:id" element={withSuspense(<BlogAdmin />)} errorElement={<RouteError />} />
        <Route path="/admin/ai-providers" element={withSuspense(<AIProvidersAdmin />)} errorElement={<RouteError />} />
        <Route path="/admin/images" element={withSuspense(<ImageManager />)} errorElement={<RouteError />} />
        <Route path="/admin/games" element={withSuspense(<GamesAdmin />)} errorElement={<RouteError />} />
      </Route>
      <Route path="*" element={withSuspense(<NotFound />)} errorElement={<RouteError />} />
    </Route>
  ),
  {
    future: {
      v7_startTransition: true
    }
  }
);
